import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, firstValueFrom, map } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  PushClavePublicaResponse,
  PushDispositivo,
  PushDispositivosResponse,
  PushEnviarPayload,
  PushEnvioResponse,
  PushEnvioResumen,
  PushEstado,
  PushPlataformaCliente,
} from '../../shared/interfaces/push.interface';
import { skipLoading } from '../interceptors/skip-loading.context';
import { esIos } from '../utils/plataforma';
import { PushAndroidService } from './push-android.service';
import { PushIosService } from './push-ios.service';

const SW_URL = '/mayorap-sw.js';
/** Endpoint que este dispositivo registró para el usuario de la sesión. */
const ENDPOINT_STORAGE_KEY = 'mayorap_push_endpoint';

@Injectable({ providedIn: 'root' })
export class PushNotificacionesService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;
  private readonly cliente: PushPlataformaCliente = esIos()
    ? inject(PushIosService)
    : inject(PushAndroidService);

  private registro: Promise<ServiceWorkerRegistration> | null = null;

  readonly estado = signal<PushEstado>('cargando');

  /** Se llama al iniciar la app: el worker también hace falta para poder instalarla. */
  registrarServiceWorker(): void {
    if (!('serviceWorker' in navigator) || this.registro) {
      return;
    }
    this.registro = navigator.serviceWorker.register(SW_URL, { scope: '/' });
    this.registro.catch((error) => console.warn('[Mayorap] No se pudo registrar el service worker', error));
  }

  async refrescarEstado(): Promise<void> {
    const bloqueo = this.cliente.bloqueo();
    if (bloqueo) {
      this.estado.set(bloqueo);
      return;
    }
    if (Notification.permission === 'denied') {
      this.estado.set('bloqueado');
      return;
    }

    try {
      const registro = await this.obtenerRegistro();
      const suscripcion = await registro.pushManager.getSubscription();
      const propio = suscripcion && suscripcion.endpoint === localStorage.getItem(ENDPOINT_STORAGE_KEY);
      this.estado.set(propio && Notification.permission === 'granted' ? 'activo' : 'inactivo');
    } catch {
      this.estado.set('no-soportado');
    }
  }

  /** Llamar directo desde el click: en iOS el permiso se pierde si hay esperas antes. */
  async activar(): Promise<void> {
    const suscripcion = await this.cliente.suscribir({
      registro: () => this.obtenerRegistro(),
      clavePublica: () => this.obtenerClavePublica(),
    });

    const json = suscripcion.toJSON();
    await firstValueFrom(
      this.http.post(
        `${this.apiUrl}/push/suscripcion`,
        {
          plataforma: this.cliente.plataforma,
          subscription: { endpoint: json.endpoint, keys: json.keys },
        },
        { context: skipLoading() },
      ),
    );

    localStorage.setItem(ENDPOINT_STORAGE_KEY, suscripcion.endpoint);
    this.estado.set('activo');
  }

  async desactivar(): Promise<void> {
    const registro = await this.obtenerRegistro();
    const suscripcion = await registro.pushManager.getSubscription();
    const endpoint = suscripcion?.endpoint ?? localStorage.getItem(ENDPOINT_STORAGE_KEY);

    if (endpoint) {
      await firstValueFrom(
        this.http.delete(`${this.apiUrl}/push/suscripcion`, {
          body: { endpoint },
          context: skipLoading(),
        }),
      );
    }
    await suscripcion?.unsubscribe();
    localStorage.removeItem(ENDPOINT_STORAGE_KEY);
    this.estado.set('inactivo');
  }

  probar(): Observable<PushEnvioResumen> {
    return this.http
      .post<PushEnvioResponse>(`${this.apiUrl}/push/prueba`, {}, { context: skipLoading() })
      .pipe(map((response) => response.data));
  }

  enviar(payload: PushEnviarPayload): Observable<PushEnvioResumen> {
    return this.http
      .post<PushEnvioResponse>(`${this.apiUrl}/push/enviar`, payload, { context: skipLoading() })
      .pipe(map((response) => response.data));
  }

  listarDispositivos(): Observable<PushDispositivo[]> {
    return this.http
      .get<PushDispositivosResponse>(`${this.apiUrl}/push/suscripciones`, { context: skipLoading() })
      .pipe(map((response) => response.data ?? []));
  }

  quitarDispositivo(id: string): Observable<void> {
    return this.http
      .delete(`${this.apiUrl}/push/suscripciones/${id}`, { context: skipLoading() })
      .pipe(map(() => undefined));
  }

  /** Antes de borrar el token: así el dispositivo deja de recibir avisos de esta cuenta. */
  desvincularAlSalir(): void {
    const endpoint = localStorage.getItem(ENDPOINT_STORAGE_KEY);
    if (!endpoint) {
      return;
    }
    localStorage.removeItem(ENDPOINT_STORAGE_KEY);
    this.http
      .delete(`${this.apiUrl}/push/suscripcion`, { body: { endpoint }, context: skipLoading() })
      .subscribe({ error: () => undefined });
  }

  private obtenerRegistro(): Promise<ServiceWorkerRegistration> {
    this.registrarServiceWorker();
    if (!this.registro) {
      return Promise.reject(new Error('Service worker no disponible'));
    }
    return this.registro.then(() => navigator.serviceWorker.ready);
  }

  private obtenerClavePublica(): Promise<string> {
    return firstValueFrom(
      this.http
        .get<PushClavePublicaResponse>(`${this.apiUrl}/push/clave-publica`, { context: skipLoading() })
        .pipe(map((response) => response.data.publicKey)),
    );
  }
}
